import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AppleCalendar } from '@demind-inc/core';

import { AppleCalendarSyncReq, calendarApi } from '../api';

interface UseSyncAppleCalendarsListParams {
  userId: string;
  startTime: string;
  endTime: string;
  appleCalendars: AppleCalendar[];
  email?: string;
  metricId?: string;
  userPersona?: string;
  shouldSyncDeletedList?: boolean;
}

export interface SyncAppleCalendarsListResponse {
  success: boolean;
  calendarIds?: string[];
  totalSyncedEvents?: number;
  totalUpdatedEvents?: number;
  totalDeletedEvents?: number;
  totalSkippedEvents?: number;
}

export function useSyncAppleCalendarsList() {
  const queryClient = useQueryClient();

  const syncAppleCalendarsListMutation = useMutation({
    mutationFn: async ({
      userId,
      startTime,
      endTime,
      appleCalendars,
      email,
      metricId,
      userPersona,
      shouldSyncDeletedList = false,
    }: UseSyncAppleCalendarsListParams) => {
      const requestBody: AppleCalendarSyncReq = {
        email,
        metricId,
        userPersona: userPersona || '',
        shouldSyncDeletedList,
        appleCalendars,
      };

      const { data } = await calendarApi.syncAppleCalendarsList(
        userId,
        startTime,
        endTime,
        requestBody
      );

      return data as SyncAppleCalendarsListResponse;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lifestack.calendar.meta'] });
      queryClient.invalidateQueries({ queryKey: ['lifestack.calendar.events'] });
      queryClient.invalidateQueries({ queryKey: ['lifestack.calendar.allDayEvents'] });
      queryClient.invalidateQueries({ queryKey: ['lifestack.user'] });
      queryClient.invalidateQueries({ queryKey: ['lifestack.preference'] });
    },
  });

  return {
    syncAppleCalendarsList: syncAppleCalendarsListMutation.mutateAsync,
    ...syncAppleCalendarsListMutation,
  };
}
